import { apiSlice } from '../apiSlice';

import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	AdvanceSeriesDetailResDTO,
	AdvanceSeriesSinglePageResDTO,
	MajorPageDTO,
	MajorPageFilter,
	SingleDetailDto,
	UnitSeriesPublishDTO,
	UnitSinglePublishDTO,
} from '../DTO/exercise/major.interface';

export const majorApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getMajorList: builder.query<
			SuccessResponse<Paginated<MajorPageDTO>>,
			MajorPageFilter
		>({
			query: (body) => ({
				url: `/web/exercise/advance/series/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['MajorList'],
		}),
		getMajorStatusList: builder.query<SuccessResponse<MajorPageDTO[]>, null>({
			query: () => ({
				url: `/web/exercise/advance/series/published`,
				method: 'GET',
			}),
		}),
		updateMajorStatus: builder.mutation<
			SuccessResponse<any>,
			{ seriesPublishDTOList: UnitSeriesPublishDTO[] }
		>({
			query: (body) => ({
				url: `/web/exercise/advance/series/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MajorList', 'MajorDetail'],
		}),
		getMajorSortList: builder.query<SuccessResponse<MajorPageDTO[]>, null>({
			query: () => ({
				url: `/web/exercise/advance/series/sort`,
				method: 'GET',
			}),
		}),
		updateMajorSort: builder.mutation<
			SuccessResponse<any>,
			{ seriesIdList: string[] }
		>({
			query: (body) => ({
				url: `/web/exercise/advance/series/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MajorList'],
		}),
		getMajorDetail: builder.query<
			SuccessResponse<AdvanceSeriesDetailResDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/advance/series/${id}`,
				method: 'GET',
			}),
			providesTags: ['MajorDetail'],
		}),
		deleteMajor: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/advance/series/${id}`,
				method: 'DELETE',
			}),
		}),
		updateMajorDetail: builder.mutation<
			SuccessResponse<any>,
			AdvanceSeriesDetailResDTO
		>({
			query: (body) => ({
				url: `/web/exercise/advance/series/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MajorDetail'],
		}),
		createMajorDetail: builder.mutation<
			SuccessResponse<any>,
			AdvanceSeriesDetailResDTO
		>({
			query: (body) => ({
				url: `/web/exercise/advance`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['MajorList'],
		}),

		getSingleList: builder.query<
			SuccessResponse<Paginated<AdvanceSeriesSinglePageResDTO>>,
			MajorPageFilter
		>({
			query: (body) => ({
				url: `/web/exercise/advance/series/${body.id}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['MajorSingleList'],
		}),
		getSingleStatus: builder.query<
			SuccessResponse<AdvanceSeriesSinglePageResDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/advance/series/${id}/sort`,
				method: 'GET',
			}),
		}),
		updateSingleStatus: builder.mutation<
			SuccessResponse<any>,
			{ singlePublishDTOList: UnitSinglePublishDTO[]; id: string }
		>({
			query: (body) => ({
				url: `/web/exercise/advance/series/${body.id}/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MajorSingleList', 'MajorSingleDetail'],
		}),

		getSingleSort: builder.query<
			SuccessResponse<AdvanceSeriesSinglePageResDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/advance/series/${id}/sort`,
				method: 'GET',
			}),
		}),
		updateSingleSort: builder.mutation<
			SuccessResponse<any>,
			{ singleIdList: string[]; id: string }
		>({
			query: (body) => ({
				url: `/web/exercise/advance/series/${body.id}/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['MajorSingleList'],
		}),
		createMarjorSingle: builder.mutation<SuccessResponse<any>, SingleDetailDto>(
			{
				query: (body) => ({
					url: `/web/exercise/advance/series/${body.seriesId}`,
					method: 'POST',
					body,
				}),
			}
		),
		updateMarjorSingle: builder.mutation<SuccessResponse<any>, SingleDetailDto>(
			{
				query: (body) => ({
					url: `/web/exercise/advance/single/${body.singleId}`,
					method: 'PUT',
					body,
				}),
				invalidatesTags: ['MajorSingleDetail'],
			}
		),
		getMarjorSingleDetail: builder.query<
			SuccessResponse<SingleDetailDto>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/advance/single/${id}`,
				method: 'GET',
			}),
			providesTags: ['MajorSingleDetail'],
		}),
		deleteMarjorSingleDetail: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/advance/single/${id}`,
				method: 'DELETE',
			}),
		}),
	}),
});

export const {
	useGetMajorListQuery,
	useGetMajorStatusListQuery,
	useUpdateMajorStatusMutation,
	useGetMajorSortListQuery,
	useUpdateMajorSortMutation,
	useGetMajorDetailQuery,
	useDeleteMajorMutation,
	useUpdateMajorDetailMutation,
	useCreateMajorDetailMutation,
	useGetSingleListQuery,
	useGetSingleStatusQuery,
	useUpdateSingleStatusMutation,
	useGetSingleSortQuery,
	useUpdateSingleSortMutation,
	useCreateMarjorSingleMutation,
	useUpdateMarjorSingleMutation,
	useGetMarjorSingleDetailQuery,
	useDeleteMarjorSingleDetailMutation,
} = majorApiSlice;
